/* eslint-disable react/destructuring-assignment */
import React, {
  useEffect, useContext, useState, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import Typography from '@digital-hig/mui/dist/Typography';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
// eslint-disable-next-line import/no-extraneous-dependencies
import EnvContext from '../common/envContext';
import PageWrapper from '../components/pageWrapperHOC';
import logger from '../common/logger';
import WTIH from '../components/WTIH';
import UnitCompletedBanner from '../components/unitCompletionBanner';
import {
  completeUnit, optionallySendModuleEvent, hasCompletedRecord, hasStartedRecord, sendCourseEvent,
} from '../common/userUtils';
import {
  ENVIRONMENT,
  ERROR_TYPE_XHR,
  ERROR_TYPE_404,
  REQUEST_FLAGS,
  VERSION,
  ITEM_TYPE_QUIZ,
  ITEM_TYPE_VIDEO,
  ITEM_TYPE_HTML,
  LRS_VERB_STARTED,
  LRS_VERB_COMPLETED,
  PAGETYPE_COURSE_UNIT,
  AA_EVENT_START,
  AA_EVENT_COMPLETE,
} from '../common/constants';
import {
  getCuratedData, fetchCuratedData, setCongratsVisible, isCongratsVisible,
} from '../store/curatedSlice';
import {
  allUnitsInModuleComplete, generateRecordFetchingList, shouldFireGradedComplete, generateLRSID,
} from '../common/utilities';
import {
  // clearLearningRecords,
  // clearLearningRecords,
  fetchingLearningRecords,
  fetchLearningRecords,
  getLearningRecords,
  getUserDetails,
  hasFetchedLearningRecords, isAuthChecked,
  // isAuthChecked,
} from '../store/userSlice';
import { setErrorState } from '../store/errorSlice';
import { UnitDetail } from './unitDetail';
import UnitPagination from '../common/unitPagination';
import CongratsModal from '../components/congratsModal';
import {
  triggerUnitComplete,
  triggerModuleStatusChange,
  triggerCourseStatusChange,
  triggerCoursePageView,
  triggerUnitConsume,
} from '../common/analyticsApi';

const Unit = ({ match, location }) => {
  const { unitId } = match.params;
  const curatedData = useSelector(getCuratedData);
  const { tutorials, unitLookup } = curatedData;
  const curUnitData = unitLookup && unitLookup[unitId];
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const appVersion = envContext[VERSION];
  const { isPreview } = envContext[REQUEST_FLAGS] || false;
  const userDetails = useSelector(getUserDetails);
  const { userid: uid } = userDetails;
  const isFetchingLearningRecords = useSelector(fetchingLearningRecords);
  const recordChecked = useSelector(hasFetchedLearningRecords);
  const authChecked = useSelector(isAuthChecked);
  const learningRecords = useSelector(getLearningRecords);
  const dispatch = useDispatch();
  const hasFiredPageView = useRef(false);
  const hasFiredUnitConsume = useRef(false);

  const [curUnitHas, setCurUnitHas] = useState('');
  const [isModuleComplete, setIsModuleComplete] = useState(false);
  const endOfContentRef = useRef();
  const [contentViewed, setContentViewed] = useState(false);
  const [hasVideoPlayed, setHasVideoPlayed] = useState(false);
  const [hasQuizPassed, setHasQuizPassed] = useState(false);
  const congratsVisible = useSelector(isCongratsVisible);

  // find current tutorial based on current unit
  const curTutorialData = tutorials?.find((tutorial) => tutorial.id === unitLookup[unitId]?.parentId) || {};

  useEffect(() => {
    setHasVideoPlayed(false);
    setContentViewed(false);
    setHasQuizPassed(false);
    hasFiredPageView.current = false;
    hasFiredUnitConsume.current = false;
  }, [location]);

  useEffect(() => {
    // Default congrats visible to false on load
    if (congratsVisible) {
      dispatch(setCongratsVisible(false));
    }
  }, []);

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (!contentViewed && entries[0].isIntersecting) {
        setContentViewed(true);
      }
    });

    intersectionObserver.observe(endOfContentRef.current);

    return () => intersectionObserver.disconnect();
  }, [unitId]);

  useEffect(() => {
    if (unitLookup && unitLookup[unitId]) {
      const curUnitTypes = unitLookup[unitId].content.map((content) => content.type);

      if (curUnitTypes.includes(ITEM_TYPE_QUIZ)) {
        setCurUnitHas(ITEM_TYPE_QUIZ);
      } else if (curUnitTypes.includes(ITEM_TYPE_VIDEO)) {
        setCurUnitHas(ITEM_TYPE_VIDEO);
      } else {
        setCurUnitHas(ITEM_TYPE_HTML);
      }
    }
  }, [unitLookup, unitId, location]);

  useEffect(() => {
    const areAllUnitsCompleted = allUnitsInModuleComplete({
      env, module: curTutorialData, learningRecords, currentCompletedId: unitId,
    });
    const isModuleCompleted = hasCompletedRecord({
      id: curTutorialData.id, learningRecords, env,
    });
    const isCompleted = (areAllUnitsCompleted && isModuleCompleted);
    setIsModuleComplete(isCompleted);
  }, [learningRecords]);

  useEffect(() => {
    if (((curUnitHas === ITEM_TYPE_HTML && contentViewed)
        || (curUnitHas === ITEM_TYPE_VIDEO && hasVideoPlayed)
        || (curUnitHas === ITEM_TYPE_QUIZ && hasQuizPassed)) && !isModuleComplete) {
      if (curatedData) {
        // This only applies to Courses
        const isCourseStarted = hasStartedRecord({ id: curatedData.id, learningRecords, env });
        if (!isCourseStarted) {
          sendCourseEvent({
            dispatch, courseOutline: curatedData, userDetails, env, verb: LRS_VERB_STARTED, appVersion,
          });
          triggerCourseStatusChange({ courseData: curatedData, status: AA_EVENT_START, env });
        }
        const quizzes = unitLookup[unitId].content.filter((content) => content.type === ITEM_TYPE_QUIZ);
        if (quizzes.length && shouldFireGradedComplete({
          courseOutline: curatedData, learningRecords, env, itemId: quizzes[0].id,
        })) {
          sendCourseEvent({
            dispatch, courseOutline: curatedData, userDetails, env, verb: LRS_VERB_COMPLETED, appVersion,
          });
          triggerCourseStatusChange({ courseData: curatedData, status: AA_EVENT_COMPLETE, env });
          dispatch(setCongratsVisible(true));
        }
      }

      optionallySendModuleEvent({
        dispatch, courseOutline: curatedData, moduleId: curTutorialData.id, unitId, userDetails, env, learningRecords, appVersion,
      });

      completeUnit({
        dispatch, unitId, userDetails, env, appVersion, curatedData,
      });

      triggerUnitComplete({
        courseData: curatedData, unitId, learningRecords, env, moduleData: curTutorialData,
      });

      // Deal with module start/completed
      triggerModuleStatusChange({
        moduleId: curTutorialData.id, courseData: curatedData, learningRecords, env, status: AA_EVENT_START,
      });
      if (allUnitsInModuleComplete({
        env, module: curTutorialData, learningRecords, currentCompletedId: unitId,
      })) {
        triggerModuleStatusChange({
          moduleId: curTutorialData.id, courseData: curatedData, learningRecords, env, status: AA_EVENT_COMPLETE,
        });
      }

      setHasVideoPlayed(false);
      setContentViewed(false);
      setHasQuizPassed(false);
    }
  }, [contentViewed, unitId, hasVideoPlayed, hasQuizPassed, learningRecords, curTutorialData, userDetails, curUnitHas, isModuleComplete, curatedData]);

  useEffect(() => {
    if (unitLookup && unitLookup[unitId] && !hasFiredUnitConsume.current) {
      const curUnitTypes = unitLookup[unitId].content.map((content) => content.type);
      if (((!curUnitTypes.includes(ITEM_TYPE_VIDEO) && contentViewed) || (curUnitTypes.includes(ITEM_TYPE_VIDEO) && hasVideoPlayed)) && !isModuleComplete) {
        triggerUnitConsume({
          courseData: curatedData, unitId, learningRecords, env, moduleData: curTutorialData,
        });
        setContentViewed(false);
        setHasVideoPlayed(false);
        hasFiredUnitConsume.current = true;
      }
    }
  }, [contentViewed, unitId, learningRecords, curTutorialData, curatedData, unitLookup, location, hasVideoPlayed, isModuleComplete]);

  useEffect(() => {
    if (!tutorials || !unitLookup || !unitLookup[unitId]) {
      // No tutorials exist in state or course in state does not match route
      dispatch(fetchCuratedData({
        match, env, isPreview,
      })).then((res) => {
        if (res.error) {
          switch (res?.payload?.statusCode) {
          case 500: {
            dispatch(setErrorState({ type: ERROR_TYPE_XHR }));
            break;
          }
          default: {
            dispatch(setErrorState({ type: ERROR_TYPE_404 }));
            break;
          }
          }
        }
      });
    } else if ((uid && recordChecked) || (authChecked && !uid)) {
      // Trigger pageview after auth user has records checked or user is determined to be unauthed
      if (!hasFiredPageView.current) {
        const learningRecs = learningRecords || [];
        const courseRecords = learningRecs[generateLRSID({ id: curatedData.id, env })];
        const unitUrl = generateLRSID({ id: unitId, env });
        const isUnitComplete = (learningRecs[unitUrl]
          && learningRecs[unitUrl].statements
          && learningRecs[unitUrl].statements.filter((rec) => rec.verb === LRS_VERB_COMPLETED).length > 0)
        || false;
        const isComplete = (courseRecords
        && courseRecords.statements
        && courseRecords.statements.length > 0
        && courseRecords.statements.filter((rec) => rec.verb === LRS_VERB_COMPLETED).length > 0);
        hasFiredPageView.current = true;
        triggerCoursePageView({
          courseData: curatedData, isComplete, unitId, isUnitComplete,
        });
      }
    }
  }, [unitId, location, tutorials, recordChecked, uid, authChecked, learningRecords]);

  useEffect(() => {
    if (unitLookup !== null && unitLookup !== undefined) {
      if (!isFetchingLearningRecords && uid && !recordChecked) {
        const recordList = generateRecordFetchingList({ curatedData, env });
        logger.info('fetching learning records: ', recordList);
        dispatch(fetchLearningRecords({
          uid, recordList, env, appVersion, oa2Token: userDetails?.oa2Token,
        })).then((res) => {
          if (res.error) {
            logger.error('ERROR fetching learning records: ', recordList);
            switch (res?.payload?.statusCode) {
            case 500: {
              dispatch(setErrorState({ type: ERROR_TYPE_XHR }));
              break;
            }
            default: {
              dispatch(setErrorState({ type: ERROR_TYPE_404 }));
              break;
            }
            }
          }
        });
      }
    }
  }, [curatedData, uid, isFetchingLearningRecords, recordChecked, unitLookup]);

  return (
    <PageWrapper>
      <UnitPagination
        pageType={PAGETYPE_COURSE_UNIT}
        data={{ title: curatedData?.title, uri: curatedData?.uri, id: curatedData?.id }}
        tutorials={curatedData?.tutorials}
        unitLookup={unitLookup || {}}
        learningRecords={learningRecords}
        unitId={unitId}
        position="top"
      />
      <RootElement className="maxWidth">
        <Grid container>
          <Grid item xs={12}>
            <UnitCompletedBanner unitId={unitId} isModuleComplete={isModuleComplete} moduleTitle={curTutorialData?.title} />
          </Grid>
          <Grid item xs={12} lg={9}>
            <Typography component="h1" variant="headline-large" className="unit-title">{curUnitData?.title}</Typography>
          </Grid>
          <Grid item xs={12}>
            <UnitDetail
              unitId={unitId}
              unit={curUnitData || {}}
              curatedData={curatedData}
              tutorialData={curTutorialData}
              setHasVideoPlayed={setHasVideoPlayed}
              setHasQuizPassed={setHasQuizPassed}
            />
          </Grid>
          <Grid item xs={12}>
            <WTIH
              unitId={unitId}
              tutorialId={curTutorialData?.id}
              tutorialName={curTutorialData?.title}
              tutorialType={curTutorialData?.isStandAlone ? 'standalone' : 'contextual'}
              curatedId={curatedData?.id}
              curatedName={curatedData?.title}
              learningMode={curatedData?.learningMode}
            />
          </Grid>
          <div ref={endOfContentRef} />
        </Grid>
      </RootElement>
      {congratsVisible && curatedData && <CongratsModal uri={curatedData?.uri} />}
    </PageWrapper>
  );
};

const RootElement = styled.div`
.MuiGrid-root.MuiGrid-item{
  padding-left: 0;
}

  margin: 0px auto 50px auto !important; 

  [class^='unitCompletionBanner']{
    margin-top: 58px;
  }
  .unitItem:not(:last-child) {
    margin-bottom: 58px;
  }
`;

Unit.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      unitId: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
};

export default React.memo(Unit);
